// // src/components/dashboard/RevenueChart/RevenueChart.jsx
// src/components/dashboard/RevenueChart/RevenueChart.jsx



/****og-code****************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************** */
import React from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  useTheme, 
  useMediaQuery, 
  Skeleton,
  alpha,
  Fade,
  IconButton,
  Chip
} from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { TrendingUp, Fullscreen, Download } from '@mui/icons-material';
import { useSelector } from 'react-redux';

const RevenueChart = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { chartData, loading, filters } = useSelector((state) => state.dashboard);

  const chartHeight = isMobile ? 280 : 400;
  const primarySeries = React.useMemo(() => chartData?.[0]?.data || [], [chartData]);
  const periodLabel = React.useMemo(() => {
    if (filters?.dateRange?.start || filters?.dateRange?.end) return 'Selected date range';
    return {
      today: 'Today',
      weekly: 'Last 7 days',
      monthly: 'This month',
      yearly: 'This year',
      all_time: 'All-time',
    }[filters?.quickFilter?.toLowerCase()] || 'Today';
  }, [filters]);

  const formatXAxisLabel = React.useCallback((value) => {
    const raw = String(value ?? '');

    // YYYY-MM (monthly)
    if (/^\d{4}-\d{2}$/.test(raw)) {
      const [year, month] = raw.split('-').map(Number);
      const d = new Date(year, month - 1, 1);
      return Number.isNaN(d.getTime())
        ? raw
        : d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }

    // YYYY-MM-DD / ISO datetime (daily)
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    return raw;
  }, []);

  const xTickValues = React.useMemo(() => {
    if (primarySeries.length <= 8) return undefined;
    const step = Math.ceil(primarySeries.length / 8);
    return primarySeries.filter((_, idx) => idx % step === 0).map((point) => point.x);
  }, [primarySeries]);

  const chartTheme = {
    background: 'transparent',
    text: {
      fontSize: 12,
      fill: theme.palette.text.primary,
      fontFamily: '"Inter", "Roboto", sans-serif',
    },
    axis: {
      domain: {
        line: {
          stroke: alpha(theme.palette.divider, 0.3),
          strokeWidth: 1,
        },
      },
      legend: {
        text: {
          fontSize: 13,
          fill: theme.palette.text.primary,
          fontWeight: 600,
        },
      },
      ticks: {
        line: {
          stroke: alpha(theme.palette.divider, 0.3),
          strokeWidth: 1,
        },
        text: {
          fontSize: 11,
          fill: theme.palette.text.secondary,
          fontWeight: 500,
        },
      },
    },
    grid: {
      line: {
        stroke: alpha(theme.palette.divider, 0.1),
        strokeWidth: 1,
        strokeDasharray: '3 6',
      },
    },
    crosshair: {
      line: {
        stroke: theme.palette.primary.main,
        strokeWidth: 2,
        strokeOpacity: 0.8,
      },
    },
    tooltip: {
      container: {
        background: theme.palette.mode === 'dark' 
          ? alpha(theme.palette.grey[900], 0.95)
          : alpha(theme.palette.background.paper, 0.95),
        color: theme.palette.text.primary,
        fontSize: 12,
        borderRadius: 12,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
        backdropFilter: 'blur(10px)',
        padding: '12px 16px',
      },
    },
  };

  if (loading) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          p: 4, 
          borderRadius: 4,
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.grey[900], 0.9)} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="30%" height={28} />
            <Skeleton variant="text" width="50%" height={20} />
          </Box>
        </Box>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={chartHeight}
          sx={{ borderRadius: 2 }}
        />
      </Paper>
    );
  }

  return (
    <Fade in timeout={600}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 4, 
          borderRadius: 4,
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.grey[900], 0.9)} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: `linear-gradient(90deg, transparent 0%, ${theme.palette.primary.main} 50%, transparent 100%)`,
            opacity: 0.4,
          }
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <TrendingUp sx={{ color: 'white', fontSize: '1.5rem' }} />
            </Box>
            <Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 800,
                  color: 'text.primary',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  letterSpacing: '-0.01em',
                }}
              >
                Revenue Overview
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: '0.9rem', fontWeight: 500 }}
              >
                {periodLabel} performance
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label="Live data"
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 28,
                '& .MuiChip-label': { px: 2 },
              }}
            />
            <IconButton 
              size="small" 
              sx={{ 
                display: { xs: 'none', sm: 'flex' },
                color: 'text.secondary',
                '&:hover': { 
                  color: 'primary.main',
                  background: alpha(theme.palette.primary.main, 0.1),
                }
              }}
            >
              <Fullscreen fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              sx={{ 
                display: { xs: 'none', sm: 'flex' },
                color: 'text.secondary',
                '&:hover': { 
                  color: 'primary.main',
                  background: alpha(theme.palette.primary.main, 0.1),
                }
              }}
            >
              <Download fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        
        {/* Chart */}
        <Box 
          sx={{ 
            height: chartHeight,
            width: {
              xs: '600px', 
              sm: '750px',  
              md: '800px', 
              lg: '1000px',
            },
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '40%',
              background: `linear-gradient(180deg, transparent 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              borderRadius: '0 0 16px 16px',
              zIndex: 0,
            }
          }}
        >
          <ResponsiveLine
            data={chartData}
            margin={{ top: 20, right: 40, bottom: 60, left: 80

             }}
            xScale={{ type: 'point' }}
            yScale={{
              type: 'linear',
              min: 'auto',
              max: 'auto',
              stacked: false,
              reverse: false,
            }}
            yFormat=" >-.2f"
            curve="catmullRom"
            axisTop={null}
            axisRight={null}
            axisBottom={{
              orient: 'bottom',
              tickSize: 0,
              tickPadding: 15,
              tickRotation: isMobile ? -20 : 0,
              tickValues: xTickValues,
              format: formatXAxisLabel,
              legend: 'Date',
              legendOffset: 40,
              legendPosition: 'middle',
            }}
            axisLeft={{
              orient: 'left',
              tickSize: 0,
              tickPadding: 15,
              tickRotation: 0,
              legend: 'Revenue',
              legendOffset: -60,
              legendPosition: 'middle',
            }}
            pointSize={10}
            pointColor={{ from: 'color' }}
            pointBorderWidth={3}
            pointBorderColor={{ from: 'serieColor', modifiers: [['darker', 0.3]] }}
            pointLabelYOffset={-12}
            enableArea={true}
            areaBaselineValue={0}
            areaOpacity={0.15}
            areaBlendMode="normal"
            useMesh={true}
            colors={[theme.palette.success.main, theme.palette.warning.main]}
            lineWidth={4}
            theme={chartTheme}
            animate={true}
            motionConfig="gentle"
            enableSlices="x"
            sliceTooltip={({ slice }) => (
              <Box
                sx={{
                  p: 2,
                  background: theme.palette.mode === 'dark' 
                    ? alpha(theme.palette.grey[900], 0.95)
                    : alpha(theme.palette.background.paper, 0.95),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)',
                  minWidth: 180,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                  {formatXAxisLabel(slice.points[0].data.x)}
                </Typography>
                {slice.points.map((point) => (
                  <Box key={point.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: point.serieColor,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {point.serieId}: {point.data.yFormatted}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            defs={[
              {
                id: 'gradientA',
                type: 'linearGradient',
                colors: [
                  { offset: 0, color: theme.palette.primary.main, opacity: 0.3 },
                  { offset: 100, color: theme.palette.primary.main, opacity: 0 },
                ],
              },
            ]}
            fill={[{ match: '*', id: 'gradientA' }]}
          />
        </Box>
      </Paper>
    </Fade>
  );
};

export default RevenueChart;

/********************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************************** */

// import React from 'react';
// import { Paper, Typography, Box, useTheme, useMediaQuery, Skeleton } from '@mui/material';
// import { ResponsiveLine } from '@nivo/line';
// import { useSelector } from 'react-redux';

// const RevenueChart = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const { chartData, loading } = useSelector((state) => state.dashboard);

//   const chartHeight = isMobile ? 240 : 320;

//   const chartTheme = {
//     background: 'transparent',
//     text: {
//       fontSize: 12,
//       fill: theme.palette.text.primary,
//     },
//     axis: {
//       domain: {
//         line: {
//           stroke: theme.palette.divider,
//           strokeWidth: 1,
//         },
//       },
//       legend: {
//         text: {
//           fontSize: 12,
//           fill: theme.palette.text.primary,
//         },
//       },
//       ticks: {
//         line: {
//           stroke: theme.palette.divider,
//           strokeWidth: 1,
//         },
//         text: {
//           fontSize: 11,
//           fill: theme.palette.text.secondary,
//         },
//       },
//     },
//     grid: {
//       line: {
//         stroke: theme.palette.divider,
//         strokeWidth: 1,
//         strokeDasharray: '2 4',
//       },
//     },
//     crosshair: {
//       line: {
//         stroke: theme.palette.primary.main,
//         strokeWidth: 1,
//         strokeOpacity: 0.75,
//       },
//     },
//     tooltip: {
//       container: {
//         background: theme.palette.background.paper,
//         color: theme.palette.text.primary,
//         fontSize: 12,
//         borderRadius: theme.shape.borderRadius,
//         boxShadow: theme.shadows[3],
//         border: `1px solid ${theme.palette.divider}`,
//       },
//     },
//   };

//   if (loading) {
//     return (
//       <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
//         <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
//           Revenue Overview
//         </Typography>
//         <Skeleton
//           variant="rectangular"
//           width="100%"
//           height={chartHeight}
//           sx={{ borderRadius: 1 }}
//         />
//       </Paper>
//     );
//   }

//   return (
//     <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
//       <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
//         Revenue Overview
//       </Typography>
      
//       <Box sx={{ height: chartHeight }}>
//         <ResponsiveLine
//           data={chartData}
//           margin={{ top: 20, right: 30, bottom: 50, left: 60 }}
//           xScale={{ type: 'point' }}
//           yScale={{
//             type: 'linear',
//             min: 'auto',
//             max: 'auto',
//             stacked: false,
//             reverse: false,
//           }}
//           yFormat=" >-.2f"
//           curve="cardinal"
//           axisTop={null}
//           axisRight={null}
//           axisBottom={{
//             orient: 'bottom',
//             tickSize: 5,
//             tickPadding: 5,
//             tickRotation: 0,
//             legend: 'Month',
//             legendOffset: 36,
//             legendPosition: 'middle',
//           }}
//           axisLeft={{
//             orient: 'left',
//             tickSize: 5,
//             tickPadding: 5,
//             tickRotation: 0,
//             legend: 'Revenue (GHS)',
//             legendOffset: -40,
//             legendPosition: 'middle',
//           }}
//           pointSize={8}
//           pointColor={{ from: 'color' }}
//           pointBorderWidth={2}
//           pointBorderColor={{ from: 'serieColor' }}
//           pointLabelYOffset={-12}
//           enableArea={true}
//           areaOpacity={0.1}
//           useMesh={true}
//           colors={[theme.palette.primary.main]}
//           theme={chartTheme}
//           animate={true}
//           motionStiffness={120}
//           motionDamping={15}
//           enableSlices="x"
//           sliceTooltip={({ slice }) => (
//             <Box
//               sx={{
//                 p: 1.5,
//                 bgcolor: 'background.paper',
//                 border: 1,
//                 borderColor: 'divider',
//                 borderRadius: 1,
//                 boxShadow: 3,
//               }}
//             >
//               <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
//                 {slice.points[0].data.xFormatted}
//               </Typography>
//               {slice.points.map((point) => (
//                 <Typography
//                   key={point.id}
//                   variant="body2"
//                   sx={{ color: point.serieColor }}
//                 >
//                   Revenue: GHS {point.data.yFormatted}
//                 </Typography>
//               ))}
//             </Box>
//           )}
//         />
//       </Box>
//     </Paper>
//   );
// };

// export default RevenueChart;